import {
  SerializableParam,
  selectorFamily,
  ReadOnlySelectorFamilyOptions,
  ReadOnlySelectorOptions,
  RecoilValueReadOnly,
  selector,
} from 'recoil';

interface EqualSelectorFamilyOptions<T, P extends SerializableParam>
  extends Pick<ReadOnlySelectorFamilyOptions<T, P>, 'key' | 'get'> {
  equals: (a: T, b: T) => boolean;
}

export function equalSelectorFamily<T, P extends SerializableParam>(
  options: EqualSelectorFamilyOptions<T, P>
) {
  const inner = selectorFamily<T, P>({
    key: `${options.key}_inner`,
    get: options.get,
  });

  const priorValues: Map<P, T | undefined> = new Map();

  return selectorFamily<T, P>({
    ...options,
    key: options.key,
    get:
      (param: P) =>
      ({ get }) => {
        const latest = get(inner(param));
        const prior = priorValues.get(param);
        if (prior != null && options.equals(latest, prior)) {
          return prior;
        }
        priorValues.set(param, latest);
        return latest;
      },
  });
}

interface EqualSelectorOptions<T> extends Pick<ReadOnlySelectorOptions<T>, 'key' | 'get'> {
  equals: (a: T, b: T) => boolean;
}

export function equalSelector<T>(options: EqualSelectorOptions<T>): RecoilValueReadOnly<T> {
  const inner = selector({
    key: `${options.key}_inner`,
    get: options.get,
  });

  let prior: T | undefined;

  return selector({
    key: options.key,
    get: ({ get }) => {
      const latest = get(inner);
      if (prior != null && options.equals(latest, prior)) {
        return prior;
      }
      prior = latest;
      return latest as T;
    },
  });
}
